import React, { useState, useEffect } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import {
  ChevronDown,
  ChevronRight,
  DollarSign,
  Users,
  LineChart,
  Settings as SettingsIcon,
  LogOut,
  Package,
  BarChart4,
  FileText,
  TrendingUp,
  Menu,
  X,
  Target,
  Database,
  Cloud,
} from "lucide-react";
import "./AppStyles.css";
import logo from "./logo.svg";

const Banner = ({ icon: Icon, title, description, color, to }) => (
  <Link
    to={to}
    className={`px-4 py-3 rounded-lg shadow-sm ${color} transform transition-all duration-200 hover:scale-102`}
  >
    <div className="flex items-center gap-3">
      <div className="p-2 bg-white rounded-full shadow-sm flex-shrink-0">
        <Icon size={18} className="text-gray-600" />
      </div>
      <div>
        <h3 className="text-sm sm:text-base font-semibold text-gray-800 leading-tight">{title}</h3>
        <p className="text-xs sm:text-sm text-gray-500 leading-tight hidden sm:block">{description}</p>
      </div>
    </div>
  </Link>
);

function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const banners = [
    {
      icon: DollarSign,
      title: "Sales Management",
      description: "Track and manage all sales transactions",
      color: "bg-green-250 hover:bg-green-100 border-l-4 border-green-500",
      to: "/home/sales",
    },
    {
      icon: Package,
      title: "Operations",
      description: "Handle day-to-day operations",
      color: "bg-blue-250 hover:bg-blue-100 border-l-4 border-blue-500",
      to: "/home/operations",
    },
    {
      icon: BarChart4,
      title: "Statistics",
      description: "View sales analytics and reports",
      color: "bg-indigo-250 hover:bg-indigo-100 border-l-4 border-indigo-500",
      to: "/home/sales-stat",
    },
    {
      icon: Target,
      title: "Sales Targets",
      description: "Track performance against goals",
      color: "bg-cyan-250 hover:bg-cyan-100 border-l-4 border-cyan-500",
      to: "/home/sales-target",
    },
  ];

  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      setUserEmail(user.email);
    }
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const handleLogout = async () => {
    const auth = getAuth();
    try {
      const currentUser = auth.currentUser;
      
      // Clear all 2FA-related localStorage data before signing out
      if (currentUser) {
        // Clear the 2FA requirement for this user
        localStorage.removeItem(`2fa_required_${currentUser.uid}`);
      }
      
      // Clear any pending 2FA info
      localStorage.removeItem("2fa_pending_email");
      localStorage.removeItem("2fa_pending_userid");
      
      console.log("🔍 Clearing all 2FA data and signing out");
      
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  const toggleMenu = (menu) => {
    setOpenMenu((prevMenu) => (prevMenu === menu ? "" : menu));
  };

  const menuItems = [
    {
      title: "Sales",
      icon: <DollarSign size={20} />,
      items: [
        { name: "Sales", icon: <FileText size={18} /> },
        { name: "Operations", icon: <Package size={18} /> },
      ],
    },
    {
      title: "Marketing",
      icon: <TrendingUp size={20} />,
      items: [
        { name: "Ads", icon: <BarChart4 size={18} /> },
      ],
    },
    {
      title: "Statistics",
      icon: <LineChart size={20} />,
      items: [
        { name: "Sales Stat", icon: <BarChart4 size={18} /> },
        { name: "Sales Target", icon: <Target size={18} /> },
      ],
    },
    {
      title: "System",
      icon: <Database size={20} />,
      items: [
        {
          name: "Logs",
          id: "logs",
          icon: <FileText size={18} />,
        },
        {
          name: "Backup & Restore",
          id: "backup-restore",
          icon: <Cloud size={18} />,
        },
      ],
    },
  ];

  return (
    <div className="home-container">
      <button
        className="fixed top-4 right-4 z-50 p-2 bg-white rounded-lg shadow-md lg:hidden"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      >
        {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <nav className={`side-menu ${isMobileMenuOpen ? "open" : ""}`}>
        <div className="logo-container">
          <Link to="/home" onClick={() => setIsMobileMenuOpen(false)}>
            <img src={logo} alt="Logo" className="logo" />
          </Link>
        </div>

        <div className="menu-sections">
          {menuItems.map((section) => (
            <div key={section.title} className="menu-section">
              <button
                onClick={() => toggleMenu(section.title)}
                className={`menu-button ${
                  openMenu === section.title ? "active" : ""
                }`}
              >
                <div className="menu-button-content">
                  {section.icon}
                  <span>{section.title}</span>
                </div>
                {openMenu === section.title ? (
                  <ChevronDown size={16} />
                ) : (
                  <ChevronRight size={16} />
                )}
              </button>
              <div
                className={`submenu ${
                  openMenu === section.title ? "open" : ""
                }`}
              >
                {section.items.map((item) => (
                  <Link
                    key={item.name}
                    to={`/home/${
                      item.id || item.name.toLowerCase().replace(" ", "-")
                    }`}
                    className={`submenu-link ${
                      location.pathname.includes(
                        item.name.toLowerCase().replace(" ", "-")
                      )
                        ? "active"
                        : ""
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.icon}
                    <span>{item.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="user-section">
          <Link
            to="/home/settings"
            className="settings-link"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <SettingsIcon size={18} />
            <span>Settings</span>
          </Link>
          <div className="user-email">
            <Users size={16} />
            <span>{userEmail}</span>
          </div>
          <button onClick={handleLogout} className="logout-button">
            <LogOut size={18} />
            <span>Logout</span>
          </button>
          <div className="text-center text-sm text-gray-500 mt-4 pb-4">
            <p>CreateVista V1.2.7</p>
          </div>
        </div>
      </nav>

      <main className={`content ${isMobileMenuOpen ? "menu-open" : ""}`}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {banners.map((banner, index) => (
            <Banner key={index} {...banner} />
          ))}
        </div>
        <Outlet />
      </main>
    </div>
  );
}

export default Home;
